import { useEffect, useMemo, useState } from 'react'
import { asWritten, cardItem, cardsOverview, sightGate, soundGate, waitingItems, type Deck, type ItemMemory, type SetGate } from '@aurora/core'
import { api } from '@aurora/convex/convex/_generated/api'
import { useAuroraStore } from '../../store/auroraStore'
import { activeChild, artifactsOfKind, childMemory } from '../../store/selectors'
import { authClient, getToken } from '../../sync/ConvexSync'
import { authReactClient } from '../../sync/auth'
import { tz } from '../../lib/dates'

export type { DeckRow } from '@aurora/core'

/**
 * Everything the cards page reads, from her deck artifacts and memory. The
 * arithmetic is core's cardsOverview (the phone renders the same), and its
 * counts come from the planner the cards engine runs, so "6 to review" here
 * is the six she will see.
 */
export function useCards() {
  const child = useAuroraStore(activeChild) as any
  const rows = useAuroraStore((s: any) => artifactsOfKind(s, 'deck')) as any[]
  const memory = useAuroraStore(childMemory)
  // A minute's resolution is plenty: due times are in days.
  const now = useMinute()
  const zone = tz(child?.timezone)
  return useMemo(() => {
    const overview = cardsOverview(rows, memory, now, zone)
    return { child, memory, now, zone, ...overview, held: pathHeld(overview.playable, memory, now) }
  }, [child, rows, memory, now, zone])
}

/** A deck's cards held for her reading path: how many, and what they teach. */
export interface PathHeld { cards: number; teaches: string[] }

/**
 * Each deck's cards that wait for her reading path, by what they teach: a
 * sight word or a sound comes in as a new card only once the path opens its
 * set (the path teaches, the deck reviews), by the same gate the session
 * plays. A deck whose every card waits shows nothing today, so its tile says
 * why instead of looking like a deck that never plays.
 */
function pathHeld(decks: Deck[], memory: Record<string, ItemMemory>, now: number): Record<string, PathHeld> {
  const sight = sightGate(decks)
  const gates = [sight, soundGate(decks, memory)].filter((g): g is SetGate => !!g)
  if (!gates.length) return {}
  const waiting = waitingItems(gates, memory, now)
  // Named as a parent reads them: a sight word as it is written (I), a sound by its letter.
  const teaches = Object.assign({}, ...gates.map((g) => (g === sight ? Object.fromEntries(Object.entries(g.items).map(([k, w]) => [k, asWritten(w)])) : g.items))) as Record<string, string>
  const out: Record<string, PathHeld> = {}
  for (const d of decks) {
    const cards = [...new Set(d.cards.map((c) => cardItem(d.id, c.id).toLowerCase()))].filter((k) => waiting.has(k))
    if (cards.length) out[d.id] = { cards: cards.length, teaches: [...new Set(cards.map((k) => teaches[k]!))] }
  }
  return out
}

export function useMinute(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])
  return now
}

export interface Attempt {
  item: string
  ok: boolean
  at: number
  ms?: number
  help?: boolean
  grade?: number
  mode?: string
}

/**
 * An item's attempts, fetched when a parent opens it. They stay on the
 * server (only the memory they built syncs), so this asks once and shows
 * nothing rather than an error if it cannot: the memory alone still says
 * how the item stands.
 */
export function useItemHistory(childId: string | undefined, items: string[]): Attempt[] | null | undefined {
  const [out, setOut] = useState<Attempt[] | null | undefined>(undefined)
  const key = items.join('|')
  useEffect(() => {
    setOut(undefined)
    const token = getToken() ?? ''
    if (!childId || !items.length || token === 'local-demo' || !import.meta.env.VITE_CONVEX_URL) {
      setOut(null)
      return
    }
    let cancelled = false
    // A legacy token session asks through a short-lived client; a Convex Auth
    // session through the authed client, with an empty token.
    const legacy = !!token
    const c = legacy ? authClient() : null
    const args = { token, childId: childId as any, items }
    const run = c ? c.query(api.itemHistory.itemHistory, args) : authReactClient.query(api.itemHistory.itemHistory, args)
    run
      .then((rows) => !cancelled && setOut(rows as Attempt[]))
      .catch(() => !cancelled && setOut(null))
      .finally(() => c?.close())
    return () => {
      cancelled = true
    }
  }, [childId, key]) // eslint-disable-line react-hooks/exhaustive-deps
  return out
}
